import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(function ToastViewport({ className, ...rest }, ref) {
  return (
    <ToastPrimitive.Viewport
      ref={ref}
      className={cn(
        'fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-4 sm:right-4 sm:top-auto sm:max-w-sm sm:flex-col',
        className,
      )}
      {...rest}
    />
  );
});

const toastVariants = cva(
  [
    'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border px-4 py-3 pr-8 shadow-lg shadow-black/10 transition-all',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-bottom-3 sm:data-[state=open]:slide-in-from-right-3',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-line bg-card text-card-foreground',
        success: 'border-success/40 bg-success-soft text-success',
        info: 'border-info/40 bg-info-soft text-info',
        warning: 'border-warning/40 bg-warning-soft text-warning',
        danger:
          'border-destructive/40 bg-[var(--danger-soft)] text-destructive',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface ToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

export const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  ToastProps
>(function Toast({ className, variant, ...rest }, ref) {
  return (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...rest}
    />
  );
});

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(function ToastTitle({ className, ...rest }, ref) {
  return (
    <ToastPrimitive.Title
      ref={ref}
      className={cn('text-sm font-semibold', className)}
      {...rest}
    />
  );
});

export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(function ToastDescription({ className, ...rest }, ref) {
  return (
    <ToastPrimitive.Description
      ref={ref}
      className={cn('text-xs leading-relaxed opacity-90', className)}
      {...rest}
    />
  );
});

export const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(function ToastClose({ className, ...rest }, ref) {
  return (
    <ToastPrimitive.Close
      ref={ref}
      className={cn(
        'absolute right-2 top-2 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring/40',
        className,
      )}
      aria-label="Close notification"
      {...rest}
    >
      <X className="size-3.5" />
    </ToastPrimitive.Close>
  );
});

export const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(function ToastAction({ className, ...rest }, ref) {
  return (
    <ToastPrimitive.Action
      ref={ref}
      className={cn(
        'ml-auto inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-line-strong bg-transparent px-3 text-xs font-medium transition-colors hover:bg-secondary/60 focus:outline-none focus:ring-2 focus:ring-ring/40',
        className,
      )}
      {...rest}
    />
  );
});
